/**
 * YUNIQ Card Editor — Web Component
 *
 * Data attributes (set by Liquid):
 *   data-motif-url       Full CDN URL of the engraving motif image
 *   data-motif-filename  Bare filename for production payload
 *   data-motif-offset-x  Optional custom motif center X (0..1)
 *   data-motif-offset-y  Optional custom motif center Y (0..1)
 *   data-motif-scale     Optional custom motif height ratio (0..1)
 *   data-texture-url     Full CDN URL of the brushed steel texture
 *   data-product-id      Shopify product ID
 */

'use strict';

const YCE_FINISH_COLORS = {
  gold_brushed:       '#c9a96e',
  gold_matt:          '#b8944a',
  gold_mirror:        '#d4af5a',
  black_matt_gold:    '#c9a96e',
  rose_brushed:       '#c9947a',
  rose_matt:          '#b8806a',
  silver_brushed:     '#b0b8c4',
  silver_matt:        '#a8b0bc',
  black_brushed:      '#2d2d2d',
  black_matt:         '#2a2a2a',
  black_mirror:       '#0a0a0a',
  carbon_black_matt:  '#141414',
  anthrazit_brushed:  '#7a818a',
  blue_brushed:       '#4b5f77',
  blue_matt:          '#3a5068',
  red_matt:           '#8e4a4c',
  purple_matt:        '#6b607f',
  pink_matt:          '#c9899a',
};

const YCE_FINISH_GROUPS = [
  { key: 'matt',    label: 'Matt',    priceAed: 750 },
  { key: 'brushed', label: 'Brushed', priceAed: 800 },
  { key: 'mirror',  label: 'Mirror',  priceAed: 950 },
];

// Default finish per signature-design product handle
const YCE_DEFAULT_FINISH = {
  'balance':             'rose_matt',
  'bitcoin-card':        'black_matt',
  'vip':                 'anthrazit_brushed',
  'hamsa':               'rose_brushed',
  'hamsa-2':             'gold_mirror',
  'eagle-yuniq':         'black_matt',
  'nazaar':              'gold_matt',
  'flight-ticket-dxb':   'black_matt_gold',
  'lion':                'black_brushed',
  'nefertiti':           'purple_matt',
  'roman-style':         'black_matt',
  'dark-dreams':         'black_mirror',
  'yacht-attitude':      'rainbow_mirror',
  'eternal-rivals':      'rainbow_mirror',
};

class YuniqCardEditor extends HTMLElement {
  // ── CONSTANTS ──────────────────────────────────────────────────────────
  static #CARD_W = 856;
  static #CARD_H = 540;
  static #NAME_MAX = 20;
  static #NOTE_MAX = 300;
  static #NAME_FONT_SIZE_BASE = 28;
  static #NAME_FONT_SIZE_MIN = 12;
  static #NAME_MAX_WIDTH = 340;
  static #NAME_LETTER_SPACING = 1.5;
  static #DEBOUNCE_MS = 130;
  static #MOTIF_HEIGHT_RATIO = 0.4;
  static #MOTIF_CENTER_Y_RATIO = 0.47;
  static #MOTIF_LAYOUT_OVERRIDES = {
    always_thank_god: {
      heightRatio: 0.2,
      centerYRatio: 0.5,
      centerXRatio: 0.68,
      maxWidthRatio: 0.56,
      avoidChip: true,
      respectFrameSafeZone: true,
    },
    spartan: {
      avoidChip: false,
    },
    your_signature_worldwide: {
      maxWidthRatio: 0.9,
    },
    discipline: {
      avoidName: false,
      respectFrameSafeZone: false,
    },
    millionaires_club_signature: {
      avoidName: false,
    },
    marina_yacht_club: {
      avoidName: false,
    },
    dark_dreams: {
      avoidName: false,
    },
    the_emperor: {
      avoidName: false,
    },
    yacht_attitude: {
      avoidName: false,
    },
  };
  static #FRAME_BOUNDS = {
    x: 0.2005,
    y: 0.238,
    w: 0.596,
    h: 0.517,
  };
  static #NAME_SAFE_ZONES = {
    none:     { insetX: 16, insetY: 14, maxWidthRatio: 0.46 },
    frame_4:  { insetX: 46, insetY: 38, maxWidthRatio: 0.37 }, // Celtic Braid
    frame_5:  { insetX: 36, insetY: 32, maxWidthRatio: 0.42 }, // Belle Époque  — more distance
    frame_6:  { insetX: 44, insetY: 36, maxWidthRatio: 0.37 }, // Baroque        — closer
    frame_7:  { insetX: 16, insetY: 14, maxWidthRatio: 0.46 }, // Classic
    frame_8:  { insetX: 22, insetY: 20, maxWidthRatio: 0.44 }, // Double Line
    frame_9:  { insetX: 42, insetY: 26, maxWidthRatio: 0.40 }, // Notched        — more right
    frame_10: { insetX: 42, insetY: 30, maxWidthRatio: 0.38 }, // Greek Key      — more down
    frame_11: { insetX: 54, insetY: 32, maxWidthRatio: 0.35 }, // Hearts         — more down
    frame_12: { insetX: 24, insetY: 20, maxWidthRatio: 0.43 }, // Fine Hearts
  };
  static #FONT_PRESETS = {
    classic: { label: 'Classic', family: '"OCR A Std","OCR A Extended","Courier New",monospace' },
    dubai: { label: 'Dubai', family: 'Dubai,"Trebuchet MS",sans-serif' },
    helvetica: { label: 'Helvetica', family: 'Helvetica,Arial,sans-serif' },
    garamond: { label: 'Garamond', family: 'Garamond,"Times New Roman",serif' },
    playfair: { label: 'Playfair', family: '"Palatino Linotype","Book Antiqua",Palatino,serif' },
  };
  // ── STATE ──────────────────────────────────────────────────────────────
  #state = {
    blankUrl: null,
    blankBackUrl: null,
    blankKey: '',
    blankLabel: '',
    blankSwatch: '',
    motifUrl: null,
    motifFilename: '',
    motifTitle: '',
    motifOffsetX: null,
    motifOffsetY: null,
    motifScale: null,
    motifScaleX: null,
    nameText: '',
    fontKey: 'classic',
    fontLabel: 'Classic',
    fontFamily: '"OCR A Std","OCR A Extended","Courier New",monospace',
    frameUrl: null,
    frameKey: 'none',
    frameLabel: '',
    backStyleUrl: null,
    backStyleKey: 'none',
    backStyleLabel: '',
    noteText: '',
    variantPrice: '',
    activePriceAed: 0,
    cardInsurance: false,
    previewSide: 'front',
  };

  #refs = {};
  #imgCache = new Map();
  #cardRectCache = new Map();
  #processedBlankCache = new Map();
  #motifMaskCache = new Map();
  #pageBgDark  = false;
  #pageBgColor = '#0f0e0c';
  #rafId = null;
  #nameDebounceTimer = null;
  #inputs = {};
  #resizeObserver = null;
  #renderToken = 0;
  #shadowFlipTimer = null;
  #shineTimer = 0;
  #bufScale = 1;
  #currentStep = 1;
  #insuranceVariantId = '';
  #productVariants = [];

  // ── LIFECYCLE ──────────────────────────────────────────────────────────

  connectedCallback() {
    this.#collectRefs();
    this.#injectChangeFinishBars();
    this.#injectNudges();
    this.#readDataAttributes();
    this.#initFontSelection();
    this.#isolateFrameRadioGroup();
    this.#isolateBackstyleRadioGroup();
    this.#injectHiddenInputs();
    if (this.dataset.noFrame === 'true') this.#applyNoFrameMode();
    this.#bindEvents();
    this.#setPreviewSide('front');
    this.#selectFirstSwatch();
    this.#updateSteps();
    this.#detectPageTheme();
    // Size canvas explicitly then render — defer one frame so layout is settled
    requestAnimationFrame(() => {
      this.#sizeCanvas();
      this.#scheduleRender();
    });
    // Keep canvas sized on container resize
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#sizeCanvas()) this.#scheduleRender();
    });
    this.#resizeObserver.observe(this.querySelector('.yce-scene') || this);
    this.#scheduleShine();
  }

  disconnectedCallback() {
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    clearTimeout(this.#nameDebounceTimer);
    clearTimeout(this.#shadowFlipTimer);
    clearTimeout(this.#shineTimer);
    this.#resizeObserver?.disconnect();
  }

  // ── Card shine ─────────────────────────────────────────────────────────

  #scheduleShine() {
    this.#shineTimer = setTimeout(() => {
      const stack = this.querySelector('.yce-card-stack');
      if (stack) {
        stack.classList.add('is-shining');
        setTimeout(() => stack.classList.remove('is-shining'), 3700);
      }
      this.#scheduleShine();
    }, 14000 + Math.random() * 8000);
  }

  // ── CANVAS SIZING ──────────────────────────────────────────────────────

  #makeOffscreen(/** @type {number} */ W, /** @type {number} */ H) {
    const c = document.createElement('canvas');
    const s = this.#bufScale;
    c.width = Math.round(W * s);
    c.height = Math.round(H * s);
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.setTransform(s, 0, 0, s, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
    }
    return { canvas: c, ctx };
  }

  #sizeCanvas() {
    const canvases = [this.#refs.canvas, this.#refs.backCanvas].filter(Boolean);
    const cardStack = this.#refs.cardStack || this.querySelector('.yce-card-stack');
    if (canvases.length === 0 || !cardStack) return false;
    const w = cardStack.clientWidth || cardStack.offsetWidth;
    if (!w) return false;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const bufW = Math.round(w * dpr);
    const bufH = Math.round(bufW * YuniqCardEditor.#CARD_H / YuniqCardEditor.#CARD_W);
    this.#bufScale = bufW / YuniqCardEditor.#CARD_W;
    let changed = false;
    canvases.forEach((canvas) => {
      // Keep layout driven by CSS to avoid mobile reflow drift.
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      if (canvas.width !== bufW || canvas.height !== bufH) {
        canvas.width = bufW;
        canvas.height = bufH;
        changed = true;
      }
    });
    return changed;
  }

  // ── SETUP ──────────────────────────────────────────────────────────────

  #collectRefs() {
    this.querySelectorAll('[data-ref]').forEach((el) => {
      this.#refs[el.dataset.ref] = el;
    });
  }

  #readDataAttributes() {
    this.#state.motifUrl = this.dataset.motifUrl || null;
    this.#state.motifFilename = this.dataset.motifFilename || '';
    this.#state.motifTitle = this.dataset.motifTitle || '';
    this.#state.motifOffsetX = this.#parseDatasetNumber(this.dataset.motifOffsetX);
    this.#state.motifOffsetY = this.#parseDatasetNumber(this.dataset.motifOffsetY);
    this.#state.motifScale = this.#parseDatasetNumber(this.dataset.motifScale);
    this.#state.motifScaleX = this.#parseDatasetNumber(this.dataset.motifScaleX);
    this.#state.variantPrice = this.dataset.variantPrice || '';
    this.#insuranceVariantId = this.dataset.insuranceVariant || '';
    try {
      this.#productVariants = JSON.parse(this.dataset.variants || '[]');
    } catch (_) {
      this.#productVariants = [];
    }
    // Init activePriceAed from the matt group default (first swatch selected)
    const initGroup = YCE_FINISH_GROUPS[0];
    const initVariant = this.#variantForGroup(initGroup.key);
    this.#state.activePriceAed = (initVariant?.price > 0) ? initVariant.price : initGroup.priceAed;
    this.#state.variantPrice   = this.#formatAed(this.#state.activePriceAed);
  }

  #parseDatasetNumber(value) {
    if (value == null || value === '') return null;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  #hasManualMotifOverrides() {
    return (
      this.#state.motifOffsetX != null ||
      this.#state.motifOffsetY != null ||
      this.#state.motifScale != null ||
      this.#state.motifScaleX != null
    );
  }

  #initFontSelection() {
    const selected = this.querySelector('.yce-font-option.is-active') || this.querySelector('.yce-font-option');
    if (!selected) return;
    this.#setFontByKey(selected.dataset.key || 'classic', false);
  }

  #applyNoFrameMode() {
    this.#refs.step3?.style.setProperty('display', 'none');
    this.querySelector('.yce-progress__pill[data-step="3"]')?.style.setProperty('display', 'none');
    if (this.#refs.noFrameNotice) this.#refs.noFrameNotice.style.display = '';
    if (this.#refs.next2Label) this.#refs.next2Label.textContent = 'Proceed to Backside';
  }

  #isolateFrameRadioGroup() {
    const radios = this.#refs.frames?.querySelectorAll('input[type="radio"]');
    if (!radios || radios.length === 0) return;
    const uuid = (window.crypto && typeof window.crypto.randomUUID === 'function')
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
    const name = `yce-frame-${this.dataset.productId || 'product'}-${uuid}`;
    radios.forEach((radio) => {
      radio.name = name;
    });
  }

  #isolateBackstyleRadioGroup() {
    const radios = this.#refs.backstyles?.querySelectorAll('input[type="radio"]');
    if (!radios || radios.length === 0) return;
    const uuid = (window.crypto && typeof window.crypto.randomUUID === 'function')
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
    const name = `yce-backstyle-${this.dataset.productId || 'product'}-${uuid}`;
    radios.forEach((radio) => {
      radio.name = name;
    });
  }

  #findProductForm() {
    const roots = [
      this.closest('.product-details'),
      this.closest('.shopify-section'),
      this.closest('section'),
      document,
    ].filter(Boolean);
    const forms = [];
    const seen = new Set();
    for (const root of roots) {
      const matches = root.querySelectorAll(
        'form[data-type="add-to-cart-form"], product-form-component form, form[action*="/cart/add"]'
      );
      matches.forEach((form) => {
        if (!seen.has(form)) {
          seen.add(form);
          forms.push(form);
        }
      });
      if (forms.length > 0) break;
    }
    return forms[0] || null;
  }

  /**
   * Find the product form and inject hidden property inputs into it.
   */
  #injectHiddenInputs() {
    const form = this.#findProductForm();

    if (!form) {
      console.warn('[YuniqCardEditor] Could not find product form — card properties will not be submitted.');
      return;
    }

    const props = ['Finish', 'Motif', 'Personalization', 'Font', 'Frame', 'Backside', 'Note', '_production_payload'];
    for (const prop of props) {
      let input = form.querySelector(`input[name="properties[${prop}]"][data-yuniq-prop]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${prop}]`;
        input.dataset.yuniqProp = prop;
        form.appendChild(input);
      }
      this.#inputs[prop] = input;
    }

    if (this.#inputs['Motif']) {
      this.#inputs['Motif'].value = this.#state.motifTitle || this.#state.motifFilename;
    }
  }

  #bindEvents() {
    // Swatch clicks
    this.#refs.swatches?.addEventListener('click', (e) => {
      const btn = e.target.closest('.yce-swatch');
      if (!btn) return;
      this.#selectSwatch(btn);
    });

    // Name input — debounced render, instant counter
    this.#refs.nameInput?.addEventListener('input', () => {
      clearTimeout(this.#nameDebounceTimer);
      this.#nameDebounceTimer = setTimeout(() => {
        this.#handleNameChange();
      }, YuniqCardEditor.#DEBOUNCE_MS);
      this.#updateCharCounter();
    });

    this.#refs.noteInput?.addEventListener('input', () => {
      this.#handleNoteChange();
      this.#updateNoteCounter();
    });

    const refs = /** @type {any} */ (this.#refs);
    refs.insuranceCheck?.addEventListener('change', () => {
      this.#state.cardInsurance = refs.insuranceCheck.checked;
      this.#renderStep5Summary();
    });
    refs.insuranceInfoBtn?.addEventListener('click', () => {
      const expanded = refs.insuranceInfoBtn.getAttribute('aria-expanded') === 'true';
      refs.insuranceInfoBtn.setAttribute('aria-expanded', String(!expanded));
      if (refs.insuranceDesc) refs.insuranceDesc.hidden = expanded;
    });

    ['consent1','consent2','consent3','consent4'].forEach(k => {
      refs[k]?.addEventListener('change', () => {
        if (refs[k]?.checked) refs[k]?.closest('.yce-consent__item')?.classList.remove('is-error');
        this.#updateConsentState();
      });
    });

    // Frame radio buttons
    this.#refs.frames?.addEventListener('change', (e) => {
      if (e.target.type !== 'radio') return;
      this.#handleFrameChange(e.target);
    });

    this.#refs.backstyles?.addEventListener('change', (e) => {
      if (e.target.type !== 'radio') return;
      this.#handleBackstyleChange(e.target);
    });

    // Font choices
    this.#refs.fontOptions?.addEventListener('click', (e) => {
      const btn = e.target.closest('.yce-font-option');
      if (!btn) return;
      this.#setFontByKey(btn.dataset.key || 'classic');
    });

    // Primary CTA
    this.#refs.proceedBtn?.addEventListener('click', () => {
      this.#proceedToCheckout().catch((err) => {
        console.error('[YuniqCardEditor] Proceed to checkout failed:', err);
        const msg = err?.message || 'Could not proceed to checkout. Please try again.';
        this.#showToast(msg);
      });
    });

    this.#refs.flipBtn?.addEventListener('click', () => {
      this.#togglePreviewSide();
    });

    // Tap-to-zoom — click anywhere on the card preview
    refs.cardStack?.addEventListener('click', () => this.#showZoom());

    // Step navigation — Next buttons
    refs.next1Btn?.addEventListener('click', () => {
      if (this.#state.blankKey) this.#activateStep(2);
    });
    refs.next2Btn?.addEventListener('click', () => {
      const noName = !this.#state.nameText.trim();
      const nextStep = this.dataset.noFrame === 'true' ? 4 : 3;
      if (noName) this.#maybeShowNudge('2', () => this.#activateStep(nextStep));
      else this.#activateStep(nextStep);
    });
    refs.next3Btn?.addEventListener('click', () => {
      const noFrame = this.#state.frameKey === 'none';
      if (noFrame) this.#maybeShowNudge('3', () => this.#activateStep(4));
      else this.#activateStep(4);
    });
    refs.next4Btn?.addEventListener('click', () => this.#activateStep(5));

    // Step navigation — Back buttons
    refs.back2Btn?.addEventListener('click', () => this.#activateStep(1));
    refs.back3Btn?.addEventListener('click', () => this.#activateStep(2));
    refs.back4Btn?.addEventListener('click', () => this.#activateStep(
      this.dataset.noFrame === 'true' ? 2 : 3
    ));
    refs.back5Btn?.addEventListener('click', () => this.#activateStep(4));

    // Progress pill navigation (go back to any visited step)
    this.querySelectorAll('.yce-progress__pill').forEach((pill) => {
      pill.addEventListener('click', () => {
        const n = Number(/** @type {HTMLElement} */ (pill).dataset.step);
        if (pill.classList.contains('is-done') && n <= this.#currentStep) {
          this.#activateStep(n);
        }
      });
    });

    // Change Finish bars (steps 2–5)
    const crefs = /** @type {any} */ (this.#refs);
    ['2', '3', '4', '5'].forEach((id) => {
      const btn    = crefs[`changeFinishBtn${id}`];
      const drawer = crefs[`changeFinishDrawer${id}`];
      btn?.addEventListener('click', () => this.#toggleFinishDrawer(id));
      drawer?.addEventListener('click', (/** @type {MouseEvent} */ e) => {
        const swatch = /** @type {HTMLElement|null} */ (/** @type {HTMLElement} */ (e.target).closest('.yce-swatch'));
        if (swatch) {
          this.#selectSwatch(swatch);
          this.#toggleFinishDrawer(id, false);
        }
      });
      crefs[`changeFinishPrev${id}`]?.addEventListener('click', () => {
        crefs[`changeFinishSwatches${id}`]?.scrollBy({ left: -300, behavior: 'smooth' });
      });
      crefs[`changeFinishNext${id}`]?.addEventListener('click', () => {
        crefs[`changeFinishSwatches${id}`]?.scrollBy({ left: 300, behavior: 'smooth' });
      });
    });
  }

  // ── Step navigation ────────────────────────────────────────────────────

  #activateStep(/** @type {number} */ n) {
    this.#currentStep = n;

    // Show only the target step
    this.querySelectorAll('.yce-step').forEach((step) => {
      step.classList.toggle('is-active', Number(/** @type {HTMLElement} */ (step).dataset.step) === n);
    });

    // Update progress pills
    this.querySelectorAll('.yce-progress__pill').forEach((pill) => {
      const pn = Number(/** @type {HTMLElement} */ (pill).dataset.step);
      pill.classList.toggle('is-active', pn === n);
      pill.classList.toggle('is-done', pn < n);
    });

    // Flip card to back for step 4 (backside engraving), front otherwise
    if (n === 4) {
      this.#setPreviewSide('back');
    } else if (this.#state.previewSide === 'back') {
      this.#setPreviewSide('front');
    }

    if (n === 5) {
      this.#renderStep5Summary();
      this.#updateConsentState();
    }

    // Scroll to top of component
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  #renderStep5Summary() {
    const refs = /** @type {any} */ (this.#refs);
    const el = refs.step5Summary;
    if (!el) return;
    const s = this.#state;

    const detailRows = [
      ['Finish',          s.blankLabel || s.blankKey || ''],
      ['Personalization', s.nameText.trim()],
      ['Frame',           s.frameKey !== 'none' ? (s.frameLabel || s.frameKey) : ''],
      ['Backside',        s.backStyleKey !== 'none' ? (s.backStyleLabel || s.backStyleKey) : ''],
    ].filter(([, v]) => v);

    const insuranceAed = s.cardInsurance ? 200 : 0;
    const totalAed     = s.activePriceAed + insuranceAed;

    const priceRows = [
      ['Card',             this.#formatAed(s.activePriceAed)],
      s.cardInsurance ? ['Card Insurance', `+AED 200.00`] : null,
      ['Total',            this.#formatAed(totalAed)],
    ].filter(Boolean);

    const detailHtml = detailRows
      .map(([k, v]) => `<div class="yce-summary__row"><span>${k}</span><span>${v}</span></div>`)
      .join('');
    const dividerHtml = s.activePriceAed > 0 ? `<div class="yce-summary__divider"></div>` : '';
    const priceHtml = s.activePriceAed > 0 ? priceRows
      .map(([k, v], i) => {
        const isTotal = i === priceRows.length - 1;
        return `<div class="yce-summary__row${isTotal ? ' yce-summary__row--total' : ''}"><span>${k}</span><span>${v}</span></div>`;
      })
      .join('') : '';

    el.innerHTML = detailHtml + dividerHtml + priceHtml;
  }

  #variantForGroup(groupKey) {
    if (!this.#productVariants.length) return null;
    const label = groupKey.charAt(0).toUpperCase() + groupKey.slice(1); // 'matt' → 'Matt'
    const byTitle = this.#productVariants.find(v =>
      (v.title || '').toLowerCase().includes(groupKey.toLowerCase())
    );
    if (byTitle) return byTitle;
    // Index fallback: matt=0, brushed=1, mirror=2
    const idx = YCE_FINISH_GROUPS.findIndex(g => g.key === groupKey);
    return this.#productVariants[idx] || this.#productVariants[0] || null;
  }

  #formatAed(aed) {
    return `AED ${aed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // ── Change Finish Bar ──────────────────────────────────────────────────

  #buildChangeFinishBar(/** @type {string} */ stepId) {
    const bar = document.createElement('button');
    bar.className = 'yce-change-finish';
    bar.type = 'button';

    const info = document.createElement('div');
    info.className = 'yce-change-finish__info';
    const dot = document.createElement('span');
    dot.className = 'yce-finish-dot';
    const name = document.createElement('span');
    name.className = 'yce-change-finish__name';
    info.append(dot, name);

    const lbl = document.createElement('span');
    lbl.className = 'yce-change-finish__btn';
    lbl.innerHTML = `Change <span class="yce-change-finish__chevron">▾</span>`;
    bar.append(info, lbl);

    const drawer = document.createElement('div');
    drawer.className = 'yce-finish-drawer';
    const refs0 = /** @type {any} */ (this.#refs);
    const swatchesClone = /** @type {HTMLElement|null} */ (refs0.swatches?.cloneNode(true));

    const refs = /** @type {any} */ (this.#refs);
    if (swatchesClone) {
      swatchesClone.removeAttribute('data-ref');

      const railWrap = document.createElement('div');
      railWrap.className = 'yce-drawer-wrap';

      const prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'yce-drawer-arrow yce-drawer-arrow--prev';
      prevBtn.setAttribute('aria-label', 'Scroll left');
      prevBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`;

      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'yce-drawer-arrow yce-drawer-arrow--next';
      nextBtn.setAttribute('aria-label', 'Scroll right');
      nextBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>`;

      railWrap.append(prevBtn, swatchesClone, nextBtn);
      drawer.appendChild(railWrap);

      refs[`changeFinishSwatches${stepId}`] = swatchesClone;
      refs[`changeFinishPrev${stepId}`]     = prevBtn;
      refs[`changeFinishNext${stepId}`]     = nextBtn;
    }

    refs[`changeFinishDot${stepId}`]    = dot;
    refs[`changeFinishName${stepId}`]   = name;
    refs[`changeFinishBtn${stepId}`]    = bar;
    refs[`changeFinishDrawer${stepId}`] = drawer;

    const wrap = document.createElement('div');
    wrap.className = 'yce-change-finish-wrap';
    wrap.append(bar, drawer);
    return wrap;
  }

  #injectChangeFinishBars() {
    ['2', '3', '4', '5'].forEach((id) => {
      const body = this.querySelector(`.yce-step[data-step="${id}"] .yce-step__body`);
      if (!body) return;
      body.insertBefore(this.#buildChangeFinishBar(id), body.firstChild);
    });
  }

  #injectNudges() {
    ['2', '3'].forEach((id) => {
      const nav = this.querySelector(`.yce-step[data-step="${id}"] .yce-step__nav`);
      if (!nav) return;
      const nudge = document.createElement('div');
      nudge.className = 'yce-nudge';
      nudge.style.display = 'none';
      const refs = /** @type {any} */ (this.#refs);
      refs[`nudge${id}`] = nudge;
      nav.parentElement?.insertBefore(nudge, nav);
    });
  }

  #maybeShowNudge(/** @type {string} */ step, /** @type {() => void} */ onContinue) {
    const ssKey = `yce_nudge_${step}`;
    if (sessionStorage.getItem(ssKey)) { onContinue(); return; }
    const refs = /** @type {any} */ (this.#refs);
    const el = refs[`nudge${step}`];
    if (!el) { onContinue(); return; }
    sessionStorage.setItem(ssKey, '1');
    const isStep2 = step === '2';
    const msg      = isStep2 ? 'No name entered — personalization makes your card truly yours.' : 'No frame selected — frames add a premium touch.';
    const addLabel = isStep2 ? 'Add Name' : 'Choose Frame';
    el.innerHTML = `<div class="yce-nudge__text">${msg}<div class="yce-nudge__actions"><button class="yce-nudge__btn yce-nudge__btn--primary yce-nudge__stay">${addLabel}</button><button class="yce-nudge__btn yce-nudge__continue">Continue →</button></div></div>`;
    el.style.display = '';
    const dismiss = () => { el.style.display = 'none'; el.innerHTML = ''; };
    el.querySelector('.yce-nudge__stay')?.addEventListener('click', dismiss);
    el.querySelector('.yce-nudge__continue')?.addEventListener('click', () => { dismiss(); onContinue(); });
  }

  #toggleFinishDrawer(/** @type {string} */ id, /** @type {boolean|undefined} */ forceOpen = undefined) {
    const refs = /** @type {any} */ (this.#refs);
    const drawer = refs[`changeFinishDrawer${id}`];
    const btn    = refs[`changeFinishBtn${id}`];
    if (!drawer) return;
    const open = forceOpen ?? !drawer.classList.contains('is-open');
    drawer.classList.toggle('is-open', open);
    btn?.classList.toggle('is-open', open);
  }

  #updateChangeFinishBars() {
    const refs = /** @type {any} */ (this.#refs);
    ['2', '3', '4', '5'].forEach((id) => {
      const dot  = refs[`changeFinishDot${id}`];
      const name = refs[`changeFinishName${id}`];
      if (dot)  dot.style.background = this.#state.blankSwatch || '#ccc';
      if (name) name.textContent     = this.#state.blankLabel  || 'Select finish';
    });
  }

  // ── Tap-to-zoom ────────────────────────────────────────────────────────

  #showZoom() {
    const _refs = /** @type {any} */ (this.#refs);
    const canvas = this.#state.previewSide === 'back'
      ? _refs.backCanvas : _refs.canvas;
    if (!canvas) return;

    // Export via tmp canvas with neutral bg to avoid black JPEG corners
    const tmp = document.createElement('canvas');
    tmp.width  = canvas.width;
    tmp.height = canvas.height;
    const tc = tmp.getContext('2d');
    if (tc) {
      tc.fillStyle = '#f7f7f5';
      tc.fillRect(0, 0, tmp.width, tmp.height);
      tc.drawImage(canvas, 0, 0);
    }
    const src = (tc ? tmp : canvas).toDataURL('image/jpeg', 0.95);

    const overlay = document.createElement('div');
    overlay.className = 'yce-zoom-overlay';
    overlay.innerHTML = `
      <div class="yce-zoom-overlay__card">
        <img class="yce-zoom-overlay__img" src="${src}" alt="Card preview" />
      </div>
      <div class="yce-zoom-overlay__bar">
        <button class="yce-zoom-overlay__close" aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M2 2l10 10M12 2L2 12"/>
          </svg>
          Close
        </button>
      </div>`;

    const close = () => {
      overlay.classList.remove('is-visible');
      setTimeout(() => overlay.remove(), 260);
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);

    overlay.addEventListener('click', (e) => {
      const t = /** @type {HTMLElement} */ (e.target);
      if (!t?.closest('.yce-zoom-overlay__card') && !t?.closest('.yce-zoom-overlay__bar')) close();
    });
    /** @type {HTMLElement|null} */ (overlay.querySelector('.yce-zoom-overlay__close'))?.addEventListener('click', close);

    document.body.appendChild(overlay);
    requestAnimationFrame(() => requestAnimationFrame(() => overlay.classList.add('is-visible')));
  }

  #selectFirstSwatch() {
    const handle = this.dataset.productHandle || '';
    const defaultKey = YCE_DEFAULT_FINISH[handle] || '';
    const target = defaultKey
      ? this.querySelector(`.yce-swatch[data-key="${defaultKey}"]`)
      : null;
    const swatch = target || this.querySelector('.yce-swatch');
    if (swatch) this.#selectSwatch(swatch, false);
  }

  // ── UI HANDLERS ────────────────────────────────────────────────────────

  #selectSwatch(btn, rerender = true) {
    this.querySelectorAll('.yce-swatch').forEach((b) => {
      b.classList.toggle('is-active', b === btn);
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
    });

    this.#state.blankUrl     = btn.dataset.url    || null;
    this.#state.blankBackUrl = btn.dataset.backUrl || null;
    this.#state.blankKey     = btn.dataset.key    || '';
    this.#state.blankLabel   = btn.dataset.label  || '';
    this.#state.blankSwatch  = YCE_FINISH_COLORS[/** @type {keyof typeof YCE_FINISH_COLORS} */ (this.#state.blankKey)] || '#ccc';

    if (this.#state.blankBackUrl) {
      this.querySelectorAll('.yce-backstyle-card-img').forEach((img) => { img.src = this.#state.blankBackUrl; });
    }

    const groupData = YCE_FINISH_GROUPS.find(g => g.key === btn.dataset.group);
    if (groupData) {
      const variant = this.#variantForGroup(groupData.key);
      const priceAed = (variant?.price > 0) ? variant.price : groupData.priceAed;
      this.#state.activePriceAed = priceAed;
      this.#state.variantPrice   = this.#formatAed(priceAed);
      if (variant?.id) this.dataset.variantId = String(variant.id);
    }

    this.#updateSteps();
    this.#updateChangeFinishBars();
    if (this.#refs.finishLabel) {
      this.#refs.finishLabel.textContent = this.#state.blankLabel || '';
    }

    if (rerender) {
      this.#scheduleRender();
    }
  }

  #handleNameChange() {
    this.#state.nameText = (this.#refs.nameInput?.value || '').slice(0, YuniqCardEditor.#NAME_MAX);
    this.#updateSteps();
    this.#scheduleRender();
  }

  #handleNoteChange() {
    this.#state.noteText = (this.#refs.noteInput?.value || '').slice(0, YuniqCardEditor.#NOTE_MAX);
    this.#updateSteps();
    this.#syncProperties();
  }

  #handleFrameChange(radio) {
    if (radio.value === 'none') {
      this.#state.frameUrl   = null;
      this.#state.frameKey   = 'none';
      this.#state.frameLabel = '';
    } else {
      this.#state.frameUrl   = radio.dataset.url   || null;
      this.#state.frameKey   = radio.value;
      this.#state.frameLabel = radio.dataset.label || '';
    }
    this.#updateSteps();
    this.#scheduleRender();
  }

  #handleBackstyleChange(radio) {
    if (radio.value === 'none') {
      this.#state.backStyleUrl = null;
      this.#state.backStyleKey = 'none';
      this.#state.backStyleLabel = '';
    } else {
      this.#state.backStyleUrl = radio.dataset.url || null;
      this.#state.backStyleKey = radio.value;
      this.#state.backStyleLabel = radio.dataset.label || '';
      this.#setPreviewSide('back');
    }
    this.#updateSteps();
    this.#scheduleRender();
  }

  #setFontByKey(key, rerender = true) {
    const preset = YuniqCardEditor.#FONT_PRESETS[key] || YuniqCardEditor.#FONT_PRESETS.classic;
    this.#state.fontKey = key in YuniqCardEditor.#FONT_PRESETS ? key : 'classic';
    this.#state.fontLabel = preset.label;
    this.#state.fontFamily = preset.family;

    this.querySelectorAll('.yce-font-option').forEach((btn) => {
      const active = (btn.dataset.key || '') === this.#state.fontKey;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (rerender) {
      this.#scheduleRender();
    }
  }

  #togglePreviewSide() {
    const next = this.#state.previewSide === 'front' ? 'back' : 'front';
    this.#setPreviewSide(next);
  }

  #setPreviewSide(side) {
    const prevSide = this.#state.previewSide;
    this.#state.previewSide = side === 'back' ? 'back' : 'front';
    const isBack = this.#state.previewSide === 'back';
    this.#refs.cardStack?.classList.toggle('is-back', isBack);
    if (prevSide && prevSide !== this.#state.previewSide) {
      this.#refs.cardStack?.classList.add('is-flipping');
      clearTimeout(this.#shadowFlipTimer);
      this.#shadowFlipTimer = setTimeout(() => {
        this.#refs.cardStack?.classList.remove('is-flipping');
      }, 620);
    }
    if (this.#refs.flipBtn) {
      this.#refs.flipBtn.textContent = isBack ? 'View front' : 'View back';
      this.#refs.flipBtn.setAttribute('aria-pressed', isBack ? 'true' : 'false');
    }
  }

  // ── STEP MANAGEMENT ────────────────────────────────────────────────────

  #updateSteps() {
    const hasFinish    = !!this.#state.blankKey;
    const hasName      = this.#state.nameText.trim().length > 0;
    const hasFrame     = this.#state.frameKey !== 'none';
    const hasBackstyle = this.#state.backStyleKey !== 'none';
    const hasNotes     = this.#state.noteText.trim().length > 0;

    const refs = /** @type {any} */ (this.#refs);

    // Done-check indicators in each step header
    refs.step1?.classList.toggle('is-done', hasFinish);
    refs.step2?.classList.toggle('is-done', hasName);
    refs.step3?.classList.toggle('is-done', hasFrame);
    refs.step4?.classList.toggle('is-done', hasBackstyle);
    refs.step5?.classList.toggle('is-done', hasNotes);

    // Enable step 1 Next only after a finish is selected
    if (refs.next1Btn) refs.next1Btn.disabled = !hasFinish;

    // Update finish label in preview footer
    if (refs.finishLabel) refs.finishLabel.textContent = this.#state.blankLabel || '';
  }

  // ── CHAR COUNTER ───────────────────────────────────────────────────────

  #updateCharCounter() {
    const input   = this.#refs.nameInput;
    const counter = this.#refs.charCount;
    if (!input || !counter) return;
    const len = Math.min(input.value.length, YuniqCardEditor.#NAME_MAX);
    counter.textContent = `${len} / ${YuniqCardEditor.#NAME_MAX}`;
    counter.classList.toggle('is-near-limit', len >= YuniqCardEditor.#NAME_MAX - 2);
  }

  #updateNoteCounter() {
    const input = this.#refs.noteInput;
    const counter = this.#refs.noteCount;
    if (!input || !counter) return;
    const len = Math.min(input.value.length, YuniqCardEditor.#NOTE_MAX);
    counter.textContent = `${len} / ${YuniqCardEditor.#NOTE_MAX}`;
    counter.classList.toggle('is-near-limit', len >= YuniqCardEditor.#NOTE_MAX - 20);
  }

  #updateConsentState() {
    const refs = /** @type {any} */ (this.#refs);
    const allChecked = ['consent1','consent2','consent3','consent4']
      .every(k => refs[k]?.checked);
    refs.proceedBtn?.classList.toggle('consent-pending', !allChecked);
    if (allChecked) refs.consentError?.classList.remove('is-visible');
  }

  #highlightMissingConsent() {
    const refs = /** @type {any} */ (this.#refs);
    let first = /** @type {Element | null} */ (null);
    ['consent1','consent2','consent3','consent4'].forEach(k => {
      const cb = refs[k];
      if (!cb) return;
      const item = cb.closest('.yce-consent__item');
      if (!cb.checked) {
        item?.classList.add('is-error');
        if (!first) first = item;
      }
    });
    refs.consentError?.classList.add('is-visible');
    first?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // ── FORM SYNC ──────────────────────────────────────────────────────────

  #syncProperties() {
    if (this.#inputs['Finish'])           this.#inputs['Finish'].value = this.#state.blankLabel || this.#state.blankKey;
    if (this.#inputs['Motif'])            this.#inputs['Motif'].value  = this.#state.motifTitle || this.#state.motifFilename;
    if (this.#inputs['Personalization'])  this.#inputs['Personalization'].value = this.#state.nameText;
    if (this.#inputs['Font'])             this.#inputs['Font'].value = this.#state.fontLabel;
    if (this.#inputs['Frame'])            this.#inputs['Frame'].value  = this.#state.frameLabel || this.#state.frameKey;
    if (this.#inputs['Backside'])         this.#inputs['Backside'].value = this.#state.backStyleLabel || this.#state.backStyleKey;
    if (this.#inputs['Note'])             this.#inputs['Note'].value = this.#state.noteText;

    if (this.#inputs['_production_payload']) {
      this.#inputs['_production_payload'].value = JSON.stringify({
        blank: this.#state.blankKey,
        motif: this.#state.motifFilename,
        name:  this.#state.nameText,
        font: this.#state.fontKey,
        frame: this.#state.frameKey === 'none' ? '' : this.#state.frameKey,
        backside: this.#state.backStyleKey === 'none' ? '' : this.#state.backStyleKey,
        notes: this.#state.noteText,
      });
    }
  }

  async #proceedToCheckout() {
    if (!['consent1','consent2','consent3','consent4'].every(k => (/** @type {any} */ (this.#refs))[k]?.checked)) {
      this.#highlightMissingConsent();
      return;
    }
    const btn = this.#refs.proceedBtn;
    const form = this.#findProductForm();

    this.#syncProperties();

    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
    }

    try {
      const formData = form ? new FormData(form) : new FormData();
      const root = window?.Shopify?.routes?.root || '/';
      const addUrl = `${root}cart/add.js`;

      // Resolve variant robustly (form, URL param, dataset).
      let variantId = formData.get('id');
      if (!variantId) {
        variantId = form?.querySelector('input[name="id"]')?.value || '';
      }
      if (!variantId) {
        variantId = new URLSearchParams(window.location.search).get('variant') || '';
      }
      if (!variantId) {
        variantId = this.dataset.variantId || '';
      }
      if (!variantId) {
        throw new Error('No variant selected');
      }

      const quantityRaw = formData.get('quantity');
      const quantity = Math.max(1, Number(quantityRaw) || 1);
      const properties = {
        Finish: this.#state.blankLabel || this.#state.blankKey || '',
        Motif: this.#state.motifTitle || this.#state.motifFilename || '',
        Personalization: this.#state.nameText || '',
        Font: this.#state.fontLabel || '',
        Frame: this.#state.frameLabel || this.#state.frameKey || '',
        Backside: this.#state.backStyleLabel || this.#state.backStyleKey || '',
        Note: this.#state.noteText || '',
      };
      if (this.#inputs['_production_payload']?.value) {
        properties._production_payload = this.#inputs['_production_payload'].value;
      }

      const res = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity,
              properties,
            },
            ...(this.#state.cardInsurance && this.#insuranceVariantId ? [{
              id: Number(this.#insuranceVariantId),
              quantity: 1,
            }] : []),
          ],
        }),
        credentials: 'same-origin',
      });

      if (!res.ok) {
        let message = `Add to cart failed (${res.status})`;
        try {
          const errorJson = await res.json();
          message = errorJson?.description || errorJson?.message || message;
        } catch (_) {
          // Keep generic message when JSON is unavailable.
        }
        throw new Error(message);
      }

      // Capture card preview from front canvas (with neutral bg so JPEG has no black)
      const canvas = /** @type {HTMLCanvasElement|null} */ ((/** @type {any} */ (this.#refs)).canvas);
      let previewUrl = '';
      if (canvas) {
        const tmp = document.createElement('canvas');
        tmp.width  = canvas.width;
        tmp.height = canvas.height;
        const tc = tmp.getContext('2d');
        if (tc) { tc.fillStyle = '#f7f7f5'; tc.fillRect(0, 0, tmp.width, tmp.height); tc.drawImage(canvas, 0, 0); }
        previewUrl = (tc ? tmp : canvas).toDataURL('image/jpeg', 0.85);
      }

      // Update Dawn cart count bubble
      await this.#refreshCartCount();

      // Show success panel
      this.#showSuccessPanel(previewUrl, root);

    } finally {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    }
  }

  async #refreshCartCount() {
    try {
      const root = window?.Shopify?.routes?.root || '/';
      const cart = await fetch(`${root}cart.js`, { credentials: 'same-origin' }).then(r => r.json());
      const count = cart.item_count || 0;
      document.querySelectorAll('.cart-count-bubble').forEach(el => {
        const visible = el.querySelector('span:not(.visually-hidden)');
        if (visible) visible.textContent = count;
        el.removeAttribute('hidden');
        /** @type {HTMLElement} */ (el).style.display = '';
      });
      const drawer = /** @type {any} */ (document.querySelector('cart-drawer'));
      if (drawer?.getSectionsToRender) drawer.renderContents?.({ cart });
    } catch (_) {}
  }

  #showSuccessPanel(/** @type {string} */ previewUrl, root = '/') {
    const refs = /** @type {any} */ (this.#refs);
    ['consent1','consent2','consent3','consent4'].forEach(k => { if (refs[k]) refs[k].checked = false; });
    this.#updateConsentState();
    document.querySelector('.yce-success-panel')?.remove();

    const s = this.#state;
    const lines = [
      ['Finish',          s.blankLabel    || s.blankKey    || ''],
      ['Motif',           s.motifTitle || s.motifFilename || ''],
      ['Personalization', s.nameText      || ''],
      ['Font',            s.fontLabel     || ''],
      ['Frame',           s.frameLabel    || s.frameKey    || ''],
      ['Backside',        s.backStyleLabel || s.backStyleKey || ''],
      ['Note',            s.noteText      || ''],
    ].filter(([, v]) => v);

    const panel = document.createElement('div');
    panel.className = 'yce-success-panel';
    panel.innerHTML = `
      <div class="yce-success-backdrop"></div>
      <div class="yce-success-sheet">
        <button class="yce-success-close" aria-label="Close">
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M2 2l10 10M12 2L2 12"/>
          </svg>
        </button>
        ${previewUrl ? `<div class="yce-success-preview"><img src="${previewUrl}" alt="Your card" /></div>` : ''}
        <div class="yce-success-body">
          <p class="yce-success-title">Added to your cart</p>
          <ul class="yce-success-summary">
            ${lines.map(([k, v]) => `<li><span>${k}</span><span>${v}</span></li>`).join('')}
          </ul>
        </div>
        <div class="yce-success-actions">
          <button class="yce-proceed-btn yce-proceed-btn--outline yce-js-view-cart">View Cart</button>
          <a href="${root}checkout" class="yce-proceed-btn">Checkout</a>
        </div>
      </div>
    `;

    const close = () => {
      panel.classList.remove('is-visible');
      panel.classList.add('is-leaving');
      setTimeout(() => panel.remove(), 340);
    };

    panel.querySelector('.yce-success-backdrop')?.addEventListener('click', close);
    panel.querySelector('.yce-success-close')?.addEventListener('click', close);

    panel.querySelector('.yce-js-view-cart')?.addEventListener('click', () => {
      close();
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        const trigger = /** @type {HTMLElement|null} */ (
          document.querySelector('[href$="/cart"]') || document.querySelector('.cart-icon-bubble')
        );
        if (trigger) trigger.click();
        else window.location.assign(`${root}cart`);
      } else {
        window.location.assign(`${root}cart`);
      }
    });

    document.body.appendChild(panel);
    requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('is-visible')));
  }

  // ── CANVAS RENDER PIPELINE ─────────────────────────────────────────────

  #scheduleRender() {
    const token = ++this.#renderToken;
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#render(token).catch(console.error);
    });
  }

  async #render(token) {
    const canvasFront = this.#refs.canvas;
    const canvasBack = this.#refs.backCanvas;
    if (!canvasFront) return;
    const ctxFront = canvasFront.getContext('2d');
    const ctxBack = canvasBack?.getContext('2d');
    if (!ctxFront) return;

    const W = YuniqCardEditor.#CARD_W;
    const H = YuniqCardEditor.#CARD_H;
    const scale = canvasFront.width / W;
    const setupCtx = (/** @type {CanvasRenderingContext2D} */ ctx) => {
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
    };
    setupCtx(ctxFront);
    if (ctxBack) setupCtx(ctxBack);
    ctxFront.clearRect(0, 0, W, H);
    if (ctxBack) ctxBack.clearRect(0, 0, W, H);
    // Fill with page background so GPU compositing layers never show a dark default
    ctxFront.fillStyle = this.#pageBgColor;
    ctxFront.fillRect(0, 0, W, H);
    if (ctxBack) { ctxBack.fillStyle = this.#pageBgColor; ctxBack.fillRect(0, 0, W, H); }

    const [blankImg, backBlankImg, motifImg, textureImg, frameImg, backStyleImg] = await Promise.all([
      this.#loadImage(this.#state.blankUrl),
      this.#loadImage(this.#state.blankBackUrl),
      this.#loadImage(this.#state.motifUrl),
      this.#loadImage(this.dataset.textureUrl || null),
      this.#loadImage(this.#state.frameUrl),
      this.#loadImage(this.#state.backStyleUrl),
    ]);
    if (token !== this.#renderToken) return;

    // Clip shimmer to actual card image bounds so it never illuminates margins
    const _shimR = this.#getBackCardRect(backBlankImg || blankImg, W, H);
    const _stk   = this.#refs.cardStack || this.querySelector('.yce-card-stack');
    if (_stk) {
      _stk.style.setProperty('--ci-t', (_shimR.y / H * 100).toFixed(2) + '%');
      _stk.style.setProperty('--ci-r', ((W - _shimR.x - _shimR.w) / W * 100).toFixed(2) + '%');
      _stk.style.setProperty('--ci-b', ((H - _shimR.y - _shimR.h) / H * 100).toFixed(2) + '%');
      _stk.style.setProperty('--ci-l', (_shimR.x / W * 100).toFixed(2) + '%');
    }

    // 1. Background card
    if (blankImg) {
      const src = this.#getStrippedBlank(blankImg) || blankImg;
      ctxFront.drawImage(src, 0, 0, W, H);
    } else {
      ctxFront.fillStyle = '#1a1a1a';
      ctxFront.fillRect(0, 0, W, H);
    }

    const cardBounds = this.#getFrameBounds(W, H);

    // 2. Engraving
    if (motifImg) {
      this.#drawEngraving(ctxFront, motifImg, textureImg, W, H);
    }

    // 3. Personalization text
    if (this.#state.nameText.trim()) {
      this.#drawName(ctxFront, this.#state.nameText.trim(), cardBounds, textureImg, W, H);
    }

    // 4. Frame overlay
    if (frameImg) {
      this.#drawFrameOverlay(ctxFront, frameImg, textureImg, cardBounds, W, H);
    }

    if (ctxBack) {
      this.#drawBackside(ctxBack, backBlankImg, blankImg, textureImg, this.#state.backStyleKey, backStyleImg, W, H);
    }

    this.#syncProperties();
  }

  #drawBackside(ctx, backBlankImg, blankImg, textureImg, backStyleKey, backStyleImg, W, H) {
    const baseBackImg = backBlankImg || blankImg || null;
    const cardRect = this.#getBackCardRect(baseBackImg, W, H);
    const radius = Math.round(cardRect.h * 0.06);
    ctx.save();
    this.#roundRect(ctx, cardRect.x, cardRect.y, cardRect.w, cardRect.h, radius);
    ctx.clip();

    if (baseBackImg) {
      // Use dedicated chip-free backside asset when available.
      ctx.drawImage(baseBackImg, 0, 0, W, H);
    } else {
      const palette = this.#getBackMetalPalette();
      const baseGrad = ctx.createLinearGradient(0, 0, 0, H);
      baseGrad.addColorStop(0, palette.top);
      baseGrad.addColorStop(0.5, palette.mid);
      baseGrad.addColorStop(1, palette.bottom);
      ctx.fillStyle = baseGrad;
      ctx.fillRect(0, 0, W, H);

      if (textureImg) {
        ctx.save();
        ctx.globalAlpha = 0.11;
        this.#drawTiledTexture(ctx, textureImg, 0, 0, W, H, 0.3);
        ctx.restore();
      }
    }

    const sheen = ctx.createLinearGradient(0, 0, W, H * 0.2);
    sheen.addColorStop(0, 'rgba(255,255,255,0)');
    sheen.addColorStop(0.5, 'rgba(255,255,255,0.08)');
    sheen.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sheen;
    ctx.fillRect(0, 0, W, H);

    const stripeY = Math.round(cardRect.y + (cardRect.h * 0.075));
    const stripeH = Math.round(cardRect.h * 0.14);
    ctx.fillStyle = '#090909';
    ctx.fillRect(cardRect.x, stripeY, cardRect.w, stripeH);

    const fieldX = Math.round(cardRect.x + (cardRect.w * 0.05));
    const fieldY = Math.round(cardRect.y + (cardRect.h * 0.34));
    const fieldW = Math.round(cardRect.w * 0.62);
    const fieldH = Math.round(cardRect.h * 0.12);
    this.#roundRect(ctx, fieldX, fieldY, fieldW, fieldH, Math.round(cardRect.h * 0.01));
    ctx.fillStyle = 'rgba(255,255,255,0.84)';
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(0,0,0,0.06)';
    ctx.stroke();

    if (backStyleKey && backStyleKey !== 'none') {
      this.#drawBackstyleOverlayV2(ctx, textureImg, cardRect, W, H, backStyleKey);
    }

    ctx.restore();
  }

  #drawBackstyleOverlayV2(ctx, textureImg, cardRect, W, H, backStyleKey) {
    const stylePresets = {
      backsite_1: {
        titleFamily: '"Helvetica Neue",Arial,sans-serif',
        bodyFamily: '"Helvetica Neue",Arial,sans-serif',
        weightTitle: '500',
        weightBody: '400',
      },
      backsite_2: {
        titleFamily: 'Garamond,"Times New Roman",serif',
        bodyFamily: 'Garamond,"Times New Roman",serif',
        weightTitle: '600',
        weightBody: '500',
      },
      backsite_3: {
        titleFamily: 'Dubai,"Trebuchet MS",sans-serif',
        bodyFamily: 'Dubai,"Trebuchet MS",sans-serif',
        weightTitle: '600',
        weightBody: '500',
      },
      backsite_4: {
        titleFamily: '"OCR A Std","OCR A Extended","Courier New",monospace',
        bodyFamily: '"OCR A Std","OCR A Extended","Courier New",monospace',
        weightTitle: '500',
        weightBody: '500',
      },
    };
    const preset = stylePresets[backStyleKey] || stylePresets.backsite_1;

    const { canvas: maskCanvas, ctx: mc } = this.#makeOffscreen(W, H);
    if (!mc) return;

    const fieldX = cardRect.x + (cardRect.w * 0.05);
    const fieldY = cardRect.y + (cardRect.h * 0.34);
    const fieldW = cardRect.w * 0.62;
    const fieldH = cardRect.h * 0.12;

    // Fixed anchors for all styles.
    const signatureX = cardRect.x + (cardRect.w * 0.055);
    const signatureY = fieldY - (cardRect.h * 0.028);
    const creditX = fieldX + fieldW + (cardRect.w * 0.032);
    const creditY = fieldY - (cardRect.h * 0.02);

    const detailsX = cardRect.x + (cardRect.w * 0.055);
    const line1Y = fieldY + fieldH + (cardRect.h * 0.105);
    const line2Y = line1Y + (cardRect.h * 0.115);
    const line3Y = line2Y + (cardRect.h * 0.115);
    const line4Y = cardRect.y + cardRect.h - (cardRect.h * 0.055);

    mc.save();
    mc.fillStyle   = '#fff';
    mc.strokeStyle = '#fff';
    mc.lineWidth   = 1;
    mc.lineJoin    = 'round';
    mc.textAlign   = 'left';
    mc.textBaseline = 'alphabetic';

    mc.font = `${preset.weightBody} ${Math.round(cardRect.h * 0.048)}px ${preset.bodyFamily}`;
    mc.strokeText('Signature', signatureX, signatureY);
    mc.fillText('Signature', signatureX, signatureY);

    mc.font = `${preset.weightTitle} ${Math.round(cardRect.h * 0.058)}px ${preset.titleFamily}`;
    mc.strokeText('Credit', creditX, creditY);
    mc.fillText('Credit', creditX, creditY);
    mc.font = `${preset.weightTitle} ${Math.round(cardRect.h * 0.072)}px ${preset.titleFamily}`;
    mc.strokeText('XXX', creditX, creditY + (cardRect.h * 0.075));
    mc.fillText('XXX', creditX, creditY + (cardRect.h * 0.075));

    mc.font = `${preset.weightTitle} ${Math.round(cardRect.h * 0.084)}px ${preset.titleFamily}`;
    mc.strokeText('Card Nr. 1234 5678 9012 3456', detailsX, line1Y);
    mc.fillText('Card Nr. 1234 5678 9012 3456', detailsX, line1Y);
    mc.font = `${preset.weightBody} ${Math.round(cardRect.h * 0.062)}px ${preset.bodyFamily}`;
    mc.strokeText('Valid Thru 12/28', detailsX, line2Y);
    mc.fillText('Valid Thru 12/28', detailsX, line2Y);

    const bottomLines = /** @type {Record<string,string>} */ ({
      backsite_1: 'Full stainless steel metal card',
      backsite_2: 'Full stainless steel metal card',
      backsite_3: 'Full stainless steel metal card',
      backsite_4: 'Full stainless steel metal card',
    });
    const bottomLine = bottomLines[backStyleKey] || 'Full stainless steel metal card';
    mc.strokeText(bottomLine, detailsX, line3Y);
    mc.fillText(bottomLine, detailsX, line3Y);
    mc.font = `${preset.weightBody} ${Math.round(cardRect.h * 0.058)}px ${preset.bodyFamily}`;
    mc.strokeText('yuniq.ae', detailsX, line4Y);
    mc.fillText('yuniq.ae', detailsX, line4Y);

    if (backStyleKey === 'backsite_4') {
      mc.globalAlpha = 0.9;
      mc.fillRect(detailsX, line1Y + (cardRect.h * 0.02), cardRect.w * 0.52, 1.2);
      mc.globalAlpha = 1;
    }
    mc.restore();

    // ── Engraving layer (same bright pipeline as front motif) ────────────────
    const { canvas: engraveCanvas, ctx: ec } = this.#makeOffscreen(W, H);
    if (!ec) return;

    const engStyle = this.#getMotifEngravingStyle();
    const isSilverBack = /silver/.test(this.#state.blankKey || '');
    this.#paintBrushedMetal(ec, textureImg, cardRect.x, cardRect.y, cardRect.w, cardRect.h, {
      seed: 53.2, sheen: isSilverBack ? 0.36 : 0.42, streak: isSilverBack ? 0.16 : 0.18, contrast: isSilverBack ? 1.16 : 1.2,
      baseTop: '#e2e2e2', baseMid: '#ffffff', baseBottom: '#c4c4c4',
      tintColor: engStyle.tintColor, tintAlpha: engStyle.tintAlpha,
      darkenAlpha: engStyle.darkenAlpha, brightenAlpha: engStyle.brightenAlpha,
    });
    ec.globalCompositeOperation = 'destination-in';
    ec.drawImage(maskCanvas, 0, 0, W, H);
    ec.globalCompositeOperation = 'source-over';

    // ── Directional depth: top-left highlight + bottom-right groove shadow ───
    const { canvas: hlCanvas, ctx: hc } = this.#makeOffscreen(W, H);
    if (hc) {
      hc.drawImage(maskCanvas, 0, 0, W, H);
      hc.globalCompositeOperation = 'source-in';
      const hlGrad = hc.createLinearGradient(cardRect.x, cardRect.y, cardRect.x + cardRect.w * 0.3, cardRect.y + cardRect.h * 0.3);
      hlGrad.addColorStop(0, 'rgba(255,255,255,0.28)');
      hlGrad.addColorStop(1, 'rgba(255,255,255,0)');
      hc.fillStyle = hlGrad;
      hc.fillRect(0, 0, W, H);
    }

    const { canvas: shCanvas, ctx: dc } = this.#makeOffscreen(W, H);
    if (dc) {
      dc.drawImage(maskCanvas, 0, 0, W, H);
      dc.globalCompositeOperation = 'source-in';
      const shGrad = dc.createLinearGradient(cardRect.x + cardRect.w, cardRect.y + cardRect.h, cardRect.x + cardRect.w * 0.7, cardRect.y + cardRect.h * 0.7);
      shGrad.addColorStop(0, 'rgba(0,0,0,0.38)');
      shGrad.addColorStop(1, 'rgba(0,0,0,0)');
      dc.fillStyle = shGrad;
      dc.fillRect(0, 0, W, H);
    }

    // ── Crisp groove outline (1px dark groove wall — no blur) ──────────────────
    const { canvas: grooveCanvas, ctx: gc } = this.#makeOffscreen(W, H);
    if (gc) {
      gc.drawImage(maskCanvas, -1,  0, W, H);
      gc.drawImage(maskCanvas,  1,  0, W, H);
      gc.drawImage(maskCanvas,  0, -1, W, H);
      gc.drawImage(maskCanvas,  0,  1, W, H);
      gc.globalCompositeOperation = 'source-in';
      gc.fillStyle = `rgba(0,0,0,${this.#getBackstyleGrooveAlpha()})`;
      gc.fillRect(0, 0, W, H);
    }

    // ── Composite: groove → engraving → depth (no shadow/blur) ────────────────
    ctx.drawImage(grooveCanvas, 0, 0, W, H);
    ctx.drawImage(engraveCanvas, 0, 0, W, H);
    ctx.drawImage(hlCanvas, 0, 0, W, H);
    ctx.drawImage(shCanvas, 0, 0, W, H);
  }

  #getBackMetalPalette() {
    const key = this.#state.blankKey || '';
    if (key === 'black_matt_gold') {
      return { top: '#1b2028', mid: '#2f3743', bottom: '#171c24' };
    }
    if (key.includes('gold')) {
      return { top: '#c7a55a', mid: '#dfc27a', bottom: '#b79143' };
    }
    if (key.includes('rose')) {
      return { top: '#be8f84', mid: '#d6aca2', bottom: '#aa7c72' };
    }
    if (key.includes('blue')) {
      return { top: '#4b5f77', mid: '#647c97', bottom: '#3f5268' };
    }
    if (key.includes('red')) {
      return { top: '#8e4a4c', mid: '#aa6064', bottom: '#783c3e' };
    }
    if (key.includes('pink')) {
      return { top: '#a47a88', mid: '#be95a3', bottom: '#8f6775' };
    }
    if (key.includes('purple')) {
      return { top: '#6b607f', mid: '#85789d', bottom: '#584c6d' };
    }
    if (key.includes('black') || key.includes('carbon')) {
      return { top: '#1b2028', mid: '#2f3743', bottom: '#171c24' };
    }
    if (key.includes('anthrazit')) {
      return { top: '#747b85', mid: '#8a919c', bottom: '#656d78' };
    }
    return { top: '#b7bcc2', mid: '#d2d7dd', bottom: '#a8afb8' };
  }

  #getBackCardRect(blankImg, W, H) {
    if (!blankImg) {
      return { x: 0, y: 0, w: W, h: H };
    }
    const cacheKey = `${this.#state.blankKey || 'blank'}|${W}x${H}`;
    const cached = this.#cardRectCache.get(cacheKey);
    if (cached) return cached;

    const fallback = { x: 0, y: 0, w: W, h: H };
    try {
      const off = document.createElement('canvas');
      off.width = W;
      off.height = H;
      const oc = off.getContext('2d');
      if (!oc) return fallback;
      oc.drawImage(blankImg, 0, 0, W, H);
      const data = oc.getImageData(0, 0, W, H).data;

      let minX = W;
      let minY = H;
      let maxX = -1;
      let maxY = -1;
      for (let y = 0; y < H; y += 1) {
        for (let x = 0; x < W; x += 1) {
          const a = data[((y * W) + x) * 4 + 3];
          if (a <= 8) continue;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }

      if (maxX < minX || maxY < minY) {
        this.#cardRectCache.set(cacheKey, fallback);
        return fallback;
      }

      const rect = {
        x: Math.max(0, minX),
        y: Math.max(0, minY),
        w: Math.min(W - minX, (maxX - minX + 1)),
        h: Math.min(H - minY, (maxY - minY + 1)),
      };

      // Guard against pathological bounds detection.
      if (rect.w < W * 0.5 || rect.h < H * 0.5) {
        this.#cardRectCache.set(cacheKey, fallback);
        return fallback;
      }

      this.#cardRectCache.set(cacheKey, rect);
      return rect;
    } catch (err) {
      this.#cardRectCache.set(cacheKey, fallback);
      return fallback;
    }
  }

  #roundRect(ctx, x, y, w, h, r) {
    const radius = Math.max(0, Math.min(r, Math.floor(Math.min(w, h) / 2)));
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  #drawEngraving(ctx, motifImg, textureImg, W, H) {
    motifImg = this.#toLumaMask(motifImg);
    const motifLayout = this.#getMotifLayout();
    const isManualLayout = this.#hasManualMotifOverrides();
    const motifAspect = motifImg.width / Math.max(1, motifImg.height);
    const isWideMotif = motifAspect >= 1.9;
    const resolvedHeightRatio = isWideMotif
      ? Math.min(motifLayout.heightRatio, 0.26)
      : motifLayout.heightRatio;
    const resolvedMaxWidthRatio = motifLayout.maxWidthRatio || (isWideMotif ? 0.74 : null);
    const resolvedCenterXRatio = motifLayout.centerXRatio ?? 0.5;
    const chipSafeRect = (!isManualLayout && motifLayout.avoidChip !== false) ? this.#getChipSafeRect(W, H) : null;
    const frameSafeRect = (!isManualLayout && motifLayout.respectFrameSafeZone && this.#state.frameKey !== 'none')
      ? this.#getFrontFrameSafeRect(W, H)
      : null;

    const maxMotifW = resolvedMaxWidthRatio ? (W * resolvedMaxWidthRatio) : Number.POSITIVE_INFINITY;
    const placement = this.#resolveMotifPlacement({
      motifImg,
      W,
      H,
      targetHeightRatio: resolvedHeightRatio,
      maxWidth: maxMotifW,
      centerXRatio: resolvedCenterXRatio,
      centerYRatio: motifLayout.centerYRatio,
      chipRect: chipSafeRect,
      safeRect: frameSafeRect,
      nameRect: motifLayout.avoidName ? this.#getNameRect(ctx, W, H) : null,
      scaleX: this.#state.motifScaleX ?? null,
    });
    const { motifW, motifH, motifX, motifY } = placement;

    // Finish-aware contour outline (inner, drawn on top after engraving)
    const { alpha: outlineAlpha, pad: outlinePad } = this.#getMotifOutlineAlpha();
    let olCanvas = null;
    if (outlineAlpha > 0) {
      const pad = outlinePad;
      const { canvas: oc_canvas, ctx: oc } = this.#makeOffscreen(W, H);
      if (oc) {
        oc.drawImage(motifImg, motifX - pad, motifY - pad, motifW + pad * 2, motifH + pad * 2);
        oc.globalCompositeOperation = 'source-in';
        oc.fillStyle = `rgba(0,0,0,${outlineAlpha})`;
        oc.fillRect(0, 0, W, H);
        olCanvas = oc_canvas;
      }
    }

    // Offscreen canvas for engraving
    const { canvas: eng, ctx: ec } = this.#makeOffscreen(W, H);
    const isGoldOnBlack = this.#state.blankKey === 'black_matt_gold';
    const isSilver = /silver/.test(this.#state.blankKey || '');

    // Base: brightened brushed-metal pipeline
    const engStyle = this.#getMotifEngravingStyle();
    this.#paintBrushedMetal(ec, textureImg, motifX, motifY, motifW, motifH, {
      seed: 11.3,
      sheen:    isGoldOnBlack ? 0.18 : (isSilver ? 0.36 : 0.42),
      streak:   isGoldOnBlack ? 0.16 : (isSilver ? 0.16 : 0.18),
      contrast: isGoldOnBlack ? 1.16 : (isSilver ? 1.16 : 1.2),
      baseTop: '#e2e2e2', baseMid: '#ffffff', baseBottom: '#c4c4c4',
      tintColor: engStyle.tintColor,
      tintAlpha: engStyle.tintAlpha,
      darkenAlpha: engStyle.darkenAlpha,
      brightenAlpha: engStyle.brightenAlpha,
    });

    // Clip to motif shape
    ec.globalCompositeOperation = 'destination-in';
    ec.drawImage(motifImg, motifX, motifY, motifW, motifH);
    ec.globalCompositeOperation = 'source-over';

    // Directional depth: top-left highlight + bottom-right groove shadow
    const { canvas: hlCanvas, ctx: hc } = this.#makeOffscreen(W, H);
    if (hc) {
      hc.drawImage(motifImg, motifX, motifY, motifW, motifH);
      hc.globalCompositeOperation = 'source-in';
      const hlGrad = hc.createLinearGradient(motifX, motifY, motifX + motifW * 0.3, motifY + motifH * 0.3);
      hlGrad.addColorStop(0, 'rgba(255,255,255,0.55)');
      hlGrad.addColorStop(1, 'rgba(255,255,255,0)');
      hc.fillStyle = hlGrad;
      hc.fillRect(0, 0, W, H);
    }

    const { canvas: shCanvas, ctx: dc } = this.#makeOffscreen(W, H);
    if (dc) {
      dc.drawImage(motifImg, motifX, motifY, motifW, motifH);
      dc.globalCompositeOperation = 'source-in';
      const shGrad = dc.createLinearGradient(motifX + motifW, motifY + motifH, motifX + motifW * 0.7, motifY + motifH * 0.7);
      shGrad.addColorStop(0, 'rgba(0,0,0,0.38)');
      shGrad.addColorStop(1, 'rgba(0,0,0,0)');
      dc.fillStyle = shGrad;
      dc.fillRect(0, 0, W, H);
    }

    // Optional groove shadow (depth effect under engraving)
    if (isGoldOnBlack) {
      const { canvas: grooveCanvas, ctx: gc } = this.#makeOffscreen(W, H);
      if (gc) {
        gc.drawImage(motifImg, motifX - 1, motifY, motifW, motifH);
        gc.drawImage(motifImg, motifX + 1, motifY, motifW, motifH);
        gc.drawImage(motifImg, motifX, motifY - 1, motifW, motifH);
        gc.drawImage(motifImg, motifX, motifY + 1, motifW, motifH);
        gc.globalCompositeOperation = 'source-in';
        gc.fillStyle = 'rgba(0,0,0,0.18)';
        gc.fillRect(0, 0, W, H);
      }
      ctx.drawImage(grooveCanvas, 0, 0, W, H);
    }

    // Outer edge border drawn first so eng covers the interior, leaving only the 1px ring
    if (olCanvas) ctx.drawImage(olCanvas, 0, 0, W, H);
    // Composite with soft outer glow
    ctx.shadowColor = isGoldOnBlack ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.22)';
    ctx.shadowBlur  = isGoldOnBlack ? 5 : 8;
    ctx.drawImage(eng, 0, 0, W, H);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur  = 0;
    ctx.drawImage(hlCanvas, 0, 0, W, H);
    ctx.drawImage(shCanvas, 0, 0, W, H);
  }

  #resolveMotifPlacement({ motifImg, W, H, targetHeightRatio, maxWidth, centerXRatio, centerYRatio, chipRect, safeRect, nameRect = null, scaleX = null }) {
    const minScale = 0.12;
    const targetMotifH = H * targetHeightRatio;
    const heightScale = targetMotifH / Math.max(1, motifImg.height);
    const widthScale = maxWidth / Math.max(1, motifImg.width);
    let motifScale = Math.max(minScale, Math.min(heightScale, widthScale));
    const placementRect = safeRect || { x: 0, y: 0, w: W, h: H };
    const bottomLimit = placementRect.y + (placementRect.h * 0.95);

    let motifW = motifImg.width * motifScale;
    let motifH = motifImg.height * motifScale;
    let motifX = (W * centerXRatio) - (motifW / 2);
    let motifY = (H * centerYRatio) - (motifH / 2);
    ({ x: motifX, y: motifY } = this.#clampRectInside(motifX, motifY, motifW, motifH, placementRect));

    for (let i = 0; i < 4; i += 1) {
      const motifRect = { x: motifX, y: motifY, w: motifW, h: motifH };
      if (!chipRect || !this.#rectsIntersect(motifRect, chipRect)) break;

      const minXRightOfChip = chipRect.x + chipRect.w + (W * 0.02);
      motifX = Math.max(motifX, minXRightOfChip);
      ({ x: motifX, y: motifY } = this.#clampRectInside(motifX, motifY, motifW, motifH, placementRect));
      const movedRect = { x: motifX, y: motifY, w: motifW, h: motifH };
      if (!this.#rectsIntersect(movedRect, chipRect)) break;

      const minYBelowChip = chipRect.y + chipRect.h + (H * 0.012);
      motifY = Math.max(motifY, minYBelowChip);
      ({ x: motifX, y: motifY } = this.#clampRectInside(motifX, motifY, motifW, motifH, placementRect));
      const shiftedRect = { x: motifX, y: motifY, w: motifW, h: motifH };
      if (!this.#rectsIntersect(shiftedRect, chipRect) && (motifY + motifH) <= bottomLimit) break;

      if ((motifY + motifH) <= bottomLimit) break;

      const allowedH = Math.max(H * 0.12, bottomLimit - motifY);
      const scaleToFitBottom = allowedH / Math.max(1, motifImg.height);
      motifScale = Math.max(minScale, Math.min(motifScale * 0.9, scaleToFitBottom));
      motifW = motifImg.width * motifScale;
      motifH = motifImg.height * motifScale;
      motifX = (W * centerXRatio) - (motifW / 2);
      ({ x: motifX, y: motifY } = this.#clampRectInside(motifX, motifY, motifW, motifH, placementRect));
    }

    // Name avoidance — shrink motif only when it actually intersects the name text rect.
    // Preserve the motif's visual center X and top Y across the resize.
    if (nameRect != null && this.#rectsIntersect({ x: motifX, y: motifY, w: motifW, h: motifH }, nameRect)) {
      const prevCenterX = motifX + motifW / 2;
      const targetH = Math.max(H * 0.08, nameRect.y - motifY);
      motifScale = Math.max(minScale, Math.min(motifScale, targetH / Math.max(1, motifImg.height)));
      motifW = motifImg.width  * motifScale;
      motifH = motifImg.height * motifScale;
      motifX = prevCenterX - motifW / 2;
      ({ x: motifX, y: motifY } = this.#clampRectInside(motifX, motifY, motifW, motifH, placementRect));
    }

    if (scaleX != null) {
      const cx = motifX + motifW / 2;
      motifW = motifW * scaleX;
      motifX = cx - motifW / 2;
    }

    return { motifW, motifH, motifX, motifY };
  }

  #getChipSafeRect(W, H) {
    const chipW = W * 0.17;
    const chipH = H * 0.18;
    const chipX = W * 0.09;
    const chipY = H * 0.235;
    const padX = W * 0.015;
    const padY = H * 0.012;
    return {
      x: chipX - padX,
      y: chipY - padY,
      w: chipW + (padX * 2),
      h: chipH + (padY * 2),
    };
  }

  #getFrontFrameSafeRect(W, H) {
    const b = YuniqCardEditor.#FRAME_BOUNDS;
    const x = W * b.x;
    const y = H * b.y;
    const w = W * b.w;
    const h = H * b.h;
    const insetX = W * 0.012;
    const insetY = H * 0.012;
    return {
      x: x + insetX,
      y: y + insetY,
      w: Math.max(1, w - (insetX * 2)),
      h: Math.max(1, h - (insetY * 2)),
    };
  }

  #getNameRect(ctx, W, H) {
    const text = this.#state.nameText?.trim();
    if (!text) return null;
    const bounds   = this.#getFrameBounds(W, H);
    const safe     = YuniqCardEditor.#NAME_SAFE_ZONES[this.#state.frameKey]
                  || YuniqCardEditor.#NAME_SAFE_ZONES.none;
    const x        = bounds.x + safe.insetX;
    const y        = bounds.y + bounds.h - safe.insetY;
    const maxWidth = Math.min(YuniqCardEditor.#NAME_MAX_WIDTH, Math.max(120, bounds.w * safe.maxWidthRatio));
    const spacing  = YuniqCardEditor.#NAME_LETTER_SPACING;
    const savedFont = ctx.font;
    let fontSize = YuniqCardEditor.#NAME_FONT_SIZE_BASE;
    ctx.font = `${fontSize}px ${this.#state.fontFamily}`;
    while (this.#measureTextWidth(ctx, text, spacing) > maxWidth && fontSize > YuniqCardEditor.#NAME_FONT_SIZE_MIN) {
      fontSize--;
      ctx.font = `${fontSize}px ${this.#state.fontFamily}`;
    }
    const textW = this.#measureTextWidth(ctx, text, spacing);
    ctx.font = savedFont;
    const pad = Math.ceil(fontSize * 0.15);
    return { x: x - pad, y: y - fontSize - pad, w: textW + pad * 2, h: fontSize + pad * 2 + Math.ceil(fontSize * 0.25) };
  }

  #clampRectInside(x, y, w, h, bounds) {
    const minX = bounds.x;
    const maxX = bounds.x + bounds.w - w;
    const minY = bounds.y;
    const maxY = bounds.y + bounds.h - h;
    return {
      x: Math.min(Math.max(x, minX), Math.max(minX, maxX)),
      y: Math.min(Math.max(y, minY), Math.max(minY, maxY)),
    };
  }

  #rectsIntersect(a, b) {
    return !(
      a.x + a.w <= b.x ||
      b.x + b.w <= a.x ||
      a.y + a.h <= b.y ||
      b.y + b.h <= a.y
    );
  }

  #getMotifLayout() {
    const base = {
      heightRatio: YuniqCardEditor.#MOTIF_HEIGHT_RATIO,
      centerYRatio: YuniqCardEditor.#MOTIF_CENTER_Y_RATIO,
      centerXRatio: 0.5,
      maxWidthRatio: null,
      avoidChip: true,
      respectFrameSafeZone: true,
      avoidName: true,
    };
    const applyManualOverrides = (layout) => ({
      ...layout,
      centerXRatio: this.#state.motifOffsetX != null
        ? Math.max(0.08, Math.min(0.92, this.#state.motifOffsetX))
        : layout.centerXRatio,
      centerYRatio: this.#state.motifOffsetY != null
        ? Math.max(0.12, Math.min(0.88, this.#state.motifOffsetY))
        : layout.centerYRatio,
      heightRatio: this.#state.motifScale != null
        ? Math.max(0.08, Math.min(0.6, this.#state.motifScale))
        : layout.heightRatio,
    });
    const filename = (this.#state.motifFilename || '').toLowerCase();
    if (!filename) return applyManualOverrides(base);
    for (const [key, override] of Object.entries(YuniqCardEditor.#MOTIF_LAYOUT_OVERRIDES)) {
      if (filename.includes(key)) {
        return applyManualOverrides({
          heightRatio: override.heightRatio ?? base.heightRatio,
          centerYRatio: override.centerYRatio ?? base.centerYRatio,
          centerXRatio: override.centerXRatio ?? base.centerXRatio,
          maxWidthRatio: override.maxWidthRatio ?? base.maxWidthRatio,
          avoidChip: override.avoidChip ?? base.avoidChip,
          respectFrameSafeZone: override.respectFrameSafeZone ?? base.respectFrameSafeZone,
          avoidName: override.avoidName ?? base.avoidName,
        });
      }
    }
    return applyManualOverrides(base);
  }

  #drawFrameOverlay(ctx, frameImg, textureImg, bounds, W, H) {
    if (!frameImg) return;

    const fw = Math.round(bounds.w);
    const fh = Math.round(bounds.h);
    const fx = Math.round(bounds.x + (bounds.w - fw) / 2);
    const fy = Math.round(bounds.y + (bounds.h - fh) / 2);

    // Build mask from frame shape.
    const { canvas: maskCanvas, ctx: mc } = this.#makeOffscreen(W, H);
    mc.drawImage(frameImg, fx, fy, fw, fh);

    const { canvas: texCanvas, ctx: tc } = this.#makeOffscreen(W, H);

    const frameTone = this.#getMotifEngravingStyle();
    this.#paintBrushedMetal(tc, textureImg, fx, fy, fw, fh, {
      seed: 37.7,
      sheen: 0.2,
      streak: 0.08,
      contrast: 0.96,
      tintColor: frameTone.tintColor,
      tintAlpha: frameTone.tintAlpha,
      darkenAlpha: frameTone.darkenAlpha,
      brightenAlpha: frameTone.brightenAlpha,
    });

    tc.globalCompositeOperation = 'destination-in';
    tc.drawImage(maskCanvas, 0, 0, W, H);
    tc.globalCompositeOperation = 'source-over';

    // ── Contour outline (drawn behind engraving) ──
    const { alpha: outlineAlpha, pad: outlinePad } = this.#getMotifOutlineAlpha();
    if (outlineAlpha > 0) {
      const { canvas: olCanvas, ctx: oc } = this.#makeOffscreen(W, H);
      if (oc) {
        oc.drawImage(frameImg, fx - outlinePad, fy - outlinePad,
                               fw + outlinePad * 2, fh + outlinePad * 2);
        oc.globalCompositeOperation = 'source-in';
        oc.fillStyle = `rgba(0,0,0,${outlineAlpha})`;
        oc.fillRect(0, 0, W, H);
        ctx.drawImage(olCanvas, 0, 0, W, H);
      }
    }
    ctx.drawImage(texCanvas, 0, 0, W, H);
  }

  #getFrameBounds(W, H) {
    return {
      x: Math.round(W * YuniqCardEditor.#FRAME_BOUNDS.x),
      y: Math.round(H * YuniqCardEditor.#FRAME_BOUNDS.y),
      w: Math.round(W * YuniqCardEditor.#FRAME_BOUNDS.w),
      h: Math.round(H * YuniqCardEditor.#FRAME_BOUNDS.h),
    };
  }

  #addNoise(targetCtx, x, y, w, h, intensity) {
    const imageData = targetCtx.getImageData(x, y, w, h);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 10) continue;
      const noise = (Math.random() - 0.5) * intensity;
      data[i]     = Math.min(255, Math.max(0, data[i]     + noise));
      data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + noise));
      data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + noise));
    }
    targetCtx.putImageData(imageData, x, y);
  }

  /** @param {CanvasRenderingContext2D} ctx @param {string} text
   *  @param {{x:number,y:number,w:number,h:number}} bounds
   *  @param {HTMLImageElement|null} textureImg @param {number} W @param {number} H */
  #drawName(ctx, text, bounds, textureImg, W, H) {
    if (!text) return;
    const spacing  = YuniqCardEditor.#NAME_LETTER_SPACING;
    const safe     = /** @type {Record<string,{insetX:number,insetY:number,maxWidthRatio:number}>} */ (YuniqCardEditor.#NAME_SAFE_ZONES)[this.#state.frameKey]
                  || YuniqCardEditor.#NAME_SAFE_ZONES.none;
    const x        = bounds.x + safe.insetX;
    const y        = bounds.y + bounds.h - safe.insetY;
    const maxWidth = Math.min(
      YuniqCardEditor.#NAME_MAX_WIDTH,
      Math.max(120, bounds.w * safe.maxWidthRatio)
    );

    let fontSize = YuniqCardEditor.#NAME_FONT_SIZE_BASE;
    ctx.font = `${fontSize}px ${this.#state.fontFamily}`;
    while (this.#measureTextWidth(ctx, text, spacing) > maxWidth
           && fontSize > YuniqCardEditor.#NAME_FONT_SIZE_MIN) {
      fontSize -= 1;
      ctx.font = `${fontSize}px ${this.#state.fontFamily}`;
    }

    const textW = this.#measureTextWidth(ctx, text, spacing);
    const pad   = Math.ceil(fontSize * 0.15);
    const bx    = x - pad;
    const by    = y - fontSize - pad;
    const bw    = Math.max(1, textW + pad * 2);
    const bh    = Math.max(1, fontSize + pad * 2 + Math.ceil(fontSize * 0.25));

    // ── 1. Text mask ──
    const { canvas: maskCanvas, ctx: mc } = this.#makeOffscreen(W, H);
    if (!mc) return;
    mc.font         = `${fontSize}px ${this.#state.fontFamily}`;
    mc.textBaseline = 'alphabetic';
    mc.fillStyle    = '#fff';
    this.#fillTextWithSpacing(mc, text, x, y, spacing);

    // ── 2. Brushed-metal layer clipped to text mask ──
    const { canvas: texCanvas, ctx: tc } = this.#makeOffscreen(W, H);
    if (!tc) return;
    const engStyle = this.#getMotifEngravingStyle();
    this.#paintBrushedMetal(tc, textureImg, bx, by, bw, bh, {
      seed: 71.3, sheen: this.#state.blankKey === 'black_matt_gold' ? 0.18 : 0.30, streak: 0.10, contrast: 1.05,
      baseTop: '#e0e0e0', baseMid: '#f8f8f8', baseBottom: '#c8c8c8',
      tintColor: engStyle.tintColor, tintAlpha: engStyle.tintAlpha,
      darkenAlpha: engStyle.darkenAlpha, brightenAlpha: engStyle.brightenAlpha,
    });
    tc.globalCompositeOperation = 'destination-in';
    tc.drawImage(maskCanvas, 0, 0, W, H);
    tc.globalCompositeOperation = 'source-over';

    // ── 2b. Contour outline (drawn behind engraved text) ──
    const { alpha: outlineAlpha, pad: outlinePad } = this.#getMotifOutlineAlpha();
    if (outlineAlpha > 0) {
      const { canvas: olCanvas, ctx: oc } = this.#makeOffscreen(W, H);
      if (oc) {
        oc.font        = `${fontSize}px ${this.#state.fontFamily}`;
        oc.textBaseline = 'alphabetic';
        oc.fillStyle   = '#fff';
        const p = outlinePad;
        /** @type {[number,number][]} */
        const offsets = [[-p,-p],[0,-p],[p,-p],[-p,0],[p,0],[-p,p],[0,p],[p,p]];
        for (const [dx, dy] of offsets) {
          this.#fillTextWithSpacing(oc, text, x + dx, y + dy, spacing);
        }
        oc.globalCompositeOperation = 'source-in';
        oc.fillStyle = `rgba(0,0,0,${outlineAlpha})`;
        oc.fillRect(0, 0, W, H);
        ctx.drawImage(olCanvas, 0, 0, W, H);
      }
    }

    // ── 3. Composite with drop-shadow ──
    ctx.save();
    ctx.shadowColor   = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur    = 3;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    ctx.drawImage(texCanvas, 0, 0, W, H);
    ctx.restore();
  }

  #paintBrushedMetal(ctx, textureImg, x, y, w, h, options = {}) {
    const seed = options.seed || 0;
    const sheen = options.sheen || 0.2;
    const streak = options.streak || 0.1;
    const contrast = options.contrast || 1;
    const tintColor = options.tintColor || 'rgb(208,208,208)';
    const tintAlpha = options.tintAlpha ?? 0.14;
    const darkenAlpha = options.darkenAlpha ?? 0.1;
    const brightenAlpha = options.brightenAlpha ?? 0.06;
    const baseTop    = /** @type {any} */ (options).baseTop    || '#d5d5d5';
    const baseMid    = /** @type {any} */ (options).baseMid    || '#eeeeee';
    const baseBottom = /** @type {any} */ (options).baseBottom || '#cbcbcb';

    // Base metal gradient
    const grad = ctx.createLinearGradient(x, y, x, y + h);
    grad.addColorStop(0, baseTop);
    grad.addColorStop(0.5, baseMid);
    grad.addColorStop(1, baseBottom);
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);

    // Deterministic brushed streaks (no flicker between renders).
    for (let iy = 0; iy < h; iy += 2) {
      const r = this.#rand(seed + iy * 13.17);
      const a = (0.02 + r * streak) * contrast;
      ctx.fillStyle = `rgba(255,255,255,${a.toFixed(4)})`;
      ctx.fillRect(x, y + iy, w, 1);
    }

    const shadow = ctx.createLinearGradient(x, y, x, y + h);
    shadow.addColorStop(0, 'rgba(0,0,0,0.14)');
    shadow.addColorStop(0.12, 'rgba(0,0,0,0)');
    shadow.addColorStop(0.88, 'rgba(0,0,0,0)');
    shadow.addColorStop(1, 'rgba(0,0,0,0.16)');
    ctx.fillStyle = shadow;
    ctx.fillRect(x, y, w, h);

    // Color-unify motif/frame metal with personalization family.
    ctx.fillStyle = this.#toRgba(tintColor, tintAlpha);
    ctx.fillRect(x, y, w, h);

    ctx.fillStyle = `rgba(0,0,0,${darkenAlpha})`;
    ctx.fillRect(x, y, w, h);

    ctx.fillStyle = `rgba(255,255,255,${brightenAlpha})`;
    ctx.fillRect(x, y, w, h);

    const sheenGrad = ctx.createLinearGradient(x, y, x + w, y + h * 0.2);
    sheenGrad.addColorStop(0, 'rgba(255,255,255,0)');
    sheenGrad.addColorStop(0.42, `rgba(255,255,255,${(sheen * 0.6).toFixed(3)})`);
    sheenGrad.addColorStop(0.5, `rgba(255,255,255,${sheen.toFixed(3)})`);
    sheenGrad.addColorStop(0.58, `rgba(255,255,255,${(sheen * 0.6).toFixed(3)})`);
    sheenGrad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sheenGrad;
    ctx.fillRect(x, y, w, h);
  }

  #drawTiledTexture(ctx, textureImg, x, y, w, h, scale = 0.34) {
    const tw = Math.max(24, Math.round(textureImg.width * scale));
    const th = Math.max(24, Math.round(textureImg.height * scale));
    for (let yy = y; yy < y + h; yy += th) {
      const dh = Math.min(th, y + h - yy);
      for (let xx = x; xx < x + w; xx += tw) {
        const dw = Math.min(tw, x + w - xx);
        ctx.drawImage(textureImg, 0, 0, textureImg.width, textureImg.height, xx, yy, dw, dh);
      }
    }
  }

  #rand(v) {
    const s = Math.sin(v * 12.9898) * 43758.5453;
    return s - Math.floor(s);
  }

  #getMotifOutlineAlpha() {
    const key = this.#state.blankKey || '';
    if (key === 'black_matt_gold') return { alpha: 0.22, pad: 1 };
    if (/gold|rose/.test(key)) return { alpha: 0.46, pad: 1 };
    return { alpha: 0, pad: 1 };
  }

  #getBackstyleGrooveAlpha() {
    const key = this.#state.blankKey || '';
    if (/silver/.test(key))         return 0.75;
    if (/gold|rose|pink/.test(key)) return 0.70;
    return 0.65;
  }

  #getMotifEngravingStyle() {
    const key = this.#state.blankKey || '';
    if (/silver/.test(key)) {
      // Black laser engraving on silver — matches real xTool output
      return { tintColor: 'rgb(0,0,0)', tintAlpha: 0.80, darkenAlpha: 0.40, brightenAlpha: 0 };
    }
    if (key === 'black_matt_gold') {
      // Gold engraving on black matt card — warm gold tint with high opacity
      return { tintColor: 'rgb(215,168,40)', tintAlpha: 0.82, darkenAlpha: 0.02, brightenAlpha: 0.24 };
    }
    if (/gold|rose|pink/.test(key)) {
      // Cool-silver on warm metal — lighter, more metallic. Edge definition comes from the outline.
      return { tintColor: 'rgb(210,210,210)', tintAlpha: 0.20, darkenAlpha: 0.24, brightenAlpha: 0.16 };
    }
    // Dark finishes — brighter stainless steel with specular sheen
    return { tintColor: 'rgb(175,182,195)', tintAlpha: 0.52, darkenAlpha: 0.08, brightenAlpha: 0.10 };
  }

  #toRgba(color, alpha) {
    const m = String(color).match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+)?\s*\)/i);
    if (!m) return `rgba(200,200,200,${alpha})`;
    return `rgba(${m[1]},${m[2]},${m[3]},${alpha})`;
  }

  #measureTextWidth(ctx, text, spacing) {
    let w = 0;
    for (let i = 0; i < text.length; i++) {
      w += ctx.measureText(text[i]).width;
      if (i < text.length - 1) w += spacing;
    }
    return w;
  }

  #fillTextWithSpacing(ctx, text, x, y, spacing) {
    let cursor = x;
    for (const char of text) {
      ctx.fillText(char, cursor, y);
      cursor += ctx.measureText(char).width + spacing;
    }
  }

  // ── Dark-theme card image processing ──────────────────────────────────

  #detectPageTheme() {
    const bg = getComputedStyle(document.documentElement).getPropertyValue('--color-background').trim();
    if (bg) this.#pageBgColor = bg;
    const m  = bg.match(/\d+/g);
    if (!m || m.length < 3) return;
    const lum = 0.299 * +m[0] + 0.587 * +m[1] + 0.114 * +m[2];
    this.#pageBgDark = lum < 100;
  }

  #stripBackground(canvas, ctx) {
    const w = canvas.width, h = canvas.height;
    const imageData = ctx.getImageData(0, 0, w, h);
    const px = imageData.data;
    let bgR = 0, bgG = 0, bgB = 0;
    for (const i of [0, w - 1, (h - 1) * w, (h - 1) * w + (w - 1)]) {
      bgR += px[i * 4]; bgG += px[i * 4 + 1]; bgB += px[i * 4 + 2];
    }
    bgR = Math.round(bgR / 4); bgG = Math.round(bgG / 4); bgB = Math.round(bgB / 4);
    if (0.299 * bgR + 0.587 * bgG + 0.114 * bgB < 150) return;
    const tol = 40;
    const isBg = (i) => {
      const p = i * 4;
      return px[p + 3] > 200 &&
             Math.abs(px[p]   - bgR) +
             Math.abs(px[p+1] - bgG) +
             Math.abs(px[p+2] - bgB) < tol * 3;
    };
    const visited = new Uint8Array(w * h);
    const queue   = new Int32Array(w * h);
    let head = 0, tail = 0;
    const enqueue = (i) => { if (!visited[i] && isBg(i)) { visited[i] = 1; queue[tail++] = i; } };
    for (let x = 0; x < w; x++) { enqueue(x); enqueue((h - 1) * w + x); }
    for (let y = 1; y < h - 1; y++) { enqueue(y * w); enqueue(y * w + w - 1); }
    while (head < tail) {
      const idx = queue[head++];
      const x = idx % w, y = (idx / w) | 0;
      if (x > 0)   enqueue(idx - 1);
      if (x < w-1) enqueue(idx + 1);
      if (y > 0)   enqueue(idx - w);
      if (y < h-1) enqueue(idx + w);
    }
    for (let i = 0; i < w * h; i++) { if (visited[i]) px[i * 4 + 3] = 0; }
    ctx.putImageData(imageData, 0, 0);
  }

  #getStrippedBlank(img) {
    if (!img) return null;
    const url = img.src || '';
    if (this.#processedBlankCache.has(url)) return this.#processedBlankCache.get(url);
    const w = img.naturalWidth  || img.width  || 856;
    const h = img.naturalHeight || img.height || 540;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) { this.#processedBlankCache.set(url, img); return img; }
    ctx.drawImage(img, 0, 0, w, h);
    // If the top-left corner is already transparent, the image has no background to strip —
    // return it directly to avoid premultiplied-alpha darkening from the canvas roundtrip.
    if (ctx.getImageData(0, 0, 1, 1).data[3] < 10) {
      this.#processedBlankCache.set(url, img);
      return img;
    }
    this.#stripBackground(canvas, ctx);
    this.#processedBlankCache.set(url, canvas);
    return canvas;
  }

  // ── IMAGE LOADING ──────────────────────────────────────────────────────

  #loadImage(url) {
    if (!url) return Promise.resolve(null);
    if (this.#imgCache.has(url)) return Promise.resolve(this.#imgCache.get(url));
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => { this.#imgCache.set(url, img); resolve(img); };
      img.onerror = () => { console.warn(`[YuniqCardEditor] Failed to load: ${url}`); resolve(null); };
      img.src = url;
    });
  }

  #toLumaMask(img) {
    if (!img) return img;
    if (this.#motifMaskCache.has(img)) return this.#motifMaskCache.get(img);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return img;
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    if (!ctx) return img;
    try {
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, w, h);
      const d = data.data;
      for (let i = 0; i < d.length; i += 4) {
        const a = d[i + 3] / 255;
        if (a <= 0) continue;
        const lum = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
        const s = 1 - lum / 255;
        d[i + 3] = Math.round(Math.max(0, Math.min(1, Math.pow(s, 0.85) * a)) * 255);
      }
      ctx.putImageData(data, 0, 0);
      this.#motifMaskCache.set(img, c);
      return c;
    } catch (_) { return img; }
  }

  #showToast(msg) {
    let toast = this.querySelector('.yce-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'yce-toast';
      this.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.remove('is-visible');
    requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('is-visible')));
    clearTimeout(toast._t);
    toast._t = setTimeout(() => toast.classList.remove('is-visible'), 4000);
  }
}

if (!customElements.get('yuniq-card-editor')) {
  customElements.define('yuniq-card-editor', YuniqCardEditor);
}
